import { useEffect, useMemo, useState } from 'react';
import type { FC, PropsWithChildren } from 'react';
import {
    Drawer as ChakraDrawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    useBreakpointValue,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import type { SlideDirection, StyleProps } from '@chakra-ui/react';
import { GetIcon } from '../../components/icons';
import { SoftLink } from '../../components/soft-link/SoftLink';
import { useDeferredRef } from '../../hooks';
import { classNames } from '../../utils/tailwind/classNames';
import { isModernBrowser } from '../../utils/utils';
import { Button } from '../button';
import { Header } from '../typography';
import type { Nullish } from '../../types/helpers';
import type { ClassValue } from '../../utils/tailwind/classNames';
import type { ButtonProps } from '../button';

export interface DrawerProps {
    id?: string;
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    isFullPage?: boolean;
    callToAction?: Nullish<
        Pick<ButtonProps, 'onClick' | 'isDisabled' | 'children' | 'isLoading'> & { href?: string }
    >;
    secondaryCallToAction?: Nullish<
        Pick<ButtonProps, 'onClick' | 'isDisabled' | 'children' | 'isLoading'> & { href?: string }
    >;
    containerStyle?: StyleProps;
    footerElement?: React.ReactNode;
    innerBodyClassName?: ClassValue;
    shouldToAddMarginBottom?: boolean;
    closeButtonAddCircle?: boolean;
    customCallToAction?: React.ReactNode;
}

export const Drawer: FC<PropsWithChildren<DrawerProps>> = ({
    id,
    children,
    title,
    isOpen,
    onClose,
    isFullPage,
    callToAction,
    secondaryCallToAction,
    containerStyle,
    footerElement,
    customCallToAction,
    innerBodyClassName,
    shouldToAddMarginBottom = true,
    closeButtonAddCircle = true,
}) => {
    const [cushionSize, setCushionSize] = useState(0);
    const [isContentScrollable, setIsContentScrollable] = useState(false);

    const [bodyRef, bodyRefSignal, bodyRefSubscriber] = useDeferredRef<HTMLDivElement>();
    const [ctaRef, ctaRefSignal, ctaRefSubscriber] = useDeferredRef<HTMLButtonElement>();

    useEffect(() => {
        if (bodyRef.current) {
            setIsContentScrollable(bodyRef.current.scrollHeight > bodyRef.current.clientHeight);
        }
    }, [bodyRef, bodyRefSignal]);

    useEffect(() => {
        if (ctaRef.current) {
            setCushionSize(ctaRef.current.clientHeight);
        }
    }, [ctaRef, ctaRefSignal]);

    const type: SlideDirection | undefined = useBreakpointValue({ xs: 'bottom', md: 'right' });

    let variant = isFullPage ? 'bottom-sheet-full' : 'bottom-sheet';
    if (!isModernBrowser) {
        variant = `${variant}_legacy`;
    }

    const callToActionButton = useMemo(
        () =>
            callToAction ? (
                <Button
                    ref={ctaRefSubscriber}
                    id="drawer-call-to-action-button"
                    isDisabled={callToAction.isDisabled ?? undefined}
                    onClick={callToAction.onClick ?? undefined}
                    isLoading={callToAction.isLoading ?? undefined}
                    variant="primary-black"
                    wide
                >
                    {callToAction.children}
                </Button>
            ) : null,
        [callToAction, ctaRefSubscriber],
    );

    const secondaryCallToActionButton = useMemo(
        () =>
            secondaryCallToAction ? (
                <Button
                    ref={ctaRefSubscriber}
                    id="drawer-secondary-call-to-action-button"
                    wide
                    isDisabled={secondaryCallToAction.isDisabled ?? undefined}
                    onClick={secondaryCallToAction.onClick ?? undefined}
                    isLoading={secondaryCallToAction.isLoading ?? undefined}
                    variant="secondary"
                    borderWidth={1.5}
                >
                    {secondaryCallToAction.children}
                </Button>
            ) : null,
        [secondaryCallToAction, ctaRefSubscriber],
    );

    return (
        <ChakraDrawer isOpen={isOpen} onClose={onClose} variant={variant} placement={type}>
            <DrawerOverlay />
            <DrawerContent
                data-testid={id}
                maxWidth={{ md: '445px', xl: '458px' }}
                padding={!title ? '1rem 1rem' : ''}
            >
                {title ? (
                    <DrawerHeader
                        display="flex"
                        alignItems="center"
                        justifyItems="center"
                        flex={isModernBrowser ? undefined : 'unset'}
                    >
                        <div className="flex-1">
                            <Header
                                id={`${id}-title`}
                                as="h4"
                                color="slt-pepper.1000"
                                variant="header4"
                                style="strong"
                            >
                                {title}
                            </Header>
                        </div>
                        <DrawerCloseButton
                            data-testid={`${id ?? 'drawer'}-close-button`}
                            position="static"
                        >
                            <GetIcon
                                name="close"
                                size="md"
                                borderWidth={closeButtonAddCircle ? '1.8px' : 0}
                            />
                        </DrawerCloseButton>
                    </DrawerHeader>
                ) : (
                    <DrawerCloseButton data-testid={`${id ?? 'drawer'}-close-button`}>
                        <GetIcon
                            name="close"
                            size="md"
                            borderWidth={closeButtonAddCircle ? '1.8px' : 0}
                        />
                    </DrawerCloseButton>
                )}

                <DrawerBody ref={bodyRefSubscriber} {...containerStyle}>
                    <div
                        className={classNames('flex flex-col', innerBodyClassName)}
                        style={{
                            marginBottom:
                                shouldToAddMarginBottom && isContentScrollable
                                    ? cushionSize
                                    : undefined,
                        }}
                    >
                        {children}
                    </div>
                </DrawerBody>
                {callToAction || secondaryCallToAction || customCallToAction ? (
                    <DrawerFooter>
                        <section
                            className="z-[100] flex w-full flex-col items-center justify-center gap-[10px]"
                            ref={ctaRef}
                        >
                            {customCallToAction ?? null}
                            {footerElement ?? null}
                            {callToAction?.href ? (
                                <SoftLink
                                    to={callToAction.href}
                                    aria-label={
                                        typeof callToAction.children === 'string'
                                            ? typeof callToAction.children
                                            : undefined
                                    }
                                    data-testid="drawer-call-to-action-link"
                                    className="w-full"
                                >
                                    {callToActionButton}
                                </SoftLink>
                            ) : (
                                callToActionButton
                            )}

                            {secondaryCallToAction?.href ? (
                                <SoftLink
                                    to={secondaryCallToAction.href}
                                    aria-label={
                                        typeof secondaryCallToAction.children === 'string'
                                            ? typeof secondaryCallToAction.children
                                            : undefined
                                    }
                                    data-testid="drawer-secondary-call-to-action-link"
                                    className="w-full"
                                >
                                    {secondaryCallToActionButton}
                                </SoftLink>
                            ) : (
                                secondaryCallToActionButton
                            )}
                        </section>
                    </DrawerFooter>
                ) : null}
            </DrawerContent>
        </ChakraDrawer>
    );
};

Drawer.propTypes = {
    id: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    isFullPage: PropTypes.bool,
    callToAction: PropTypes.shape({
        onClick: PropTypes.func,
        isDisabled: PropTypes.bool,
        isLoading: PropTypes.bool,
        children: PropTypes.node,
        href: PropTypes.string,
    }),
    secondaryCallToAction: PropTypes.shape({
        onClick: PropTypes.func,
        isDisabled: PropTypes.bool,
        isLoading: PropTypes.bool,
        children: PropTypes.node,
        href: PropTypes.string,
    }),
    containerStyle: PropTypes.object,
};

Drawer.defaultProps = {
    id: undefined,
    isFullPage: false,
    callToAction: undefined,
    secondaryCallToAction: undefined,
    containerStyle: undefined,
    title: undefined,
    footerElement: undefined,
    innerBodyClassName: undefined,
    shouldToAddMarginBottom: true,
    closeButtonAddCircle: true,
    customCallToAction: null,
};
